import {
  forwardRef,
  useEffect,
  useState,
  type ChangeEvent,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  TextField,
  Grid,
  Container,
  Select,
  MenuItem,
  Box,
  type SelectChangeEvent,
} from "@mui/material";
import {
  ShepTooltip,
  ShepPagination,
  ShepTab,
  ShepTabs,
  ShepDataGridPro,
} from "@/components/GenericComponents/ShepComponents";
import { Close as CloseIcon } from "@mui/icons-material";
import ShepButton from "@/components/GenericComponents/ShepButton";
import { useSelector } from "react-redux";
import { defaultCode } from "@/features/connections/integrationSlice";
import {
  fetchCatalogSummaries,
  selectCatalogCustomFieldsLoaded,
  selectAllCatalogCustomFields,
  fetchCatalogCustomFields,
  type ICustomFieldDto,
} from "@/features/maintenance/maintenanceSlice";
import { useGridApiContext } from "@mui/x-data-grid-pro";
import { CatalogTypesExt } from "./MaintenanceTypes";
import { createAlert } from "@/features/homepage/alertSliceList";
import axios from "@/features/axiosHelper";
import { DeleteBinFill, EditIcon } from "../../ImageRepo";
import { useGridApiRef, type GridRenderCellParams } from "@mui/x-data-grid";
import ShepHorizontalMenu from "@/components/GenericComponents/ShepHorizontalMenu";
import { useAppDispatch } from "@/typedDispatch";
import type { UUIDTypes } from "uuid";

interface ICustomCatalogFieldPopup {
  open: boolean;
  onClose: () => void;
}
const CustomCatalogFieldPopup = ({ open, onClose }: ICustomCatalogFieldPopup) => {
  const dispatch = useAppDispatch();

  const [tabValue, setTabValue] = useState(1);

  const handleTabChange = (_event: Event, newValue: number) => {
    setTabValue(newValue);
  };
  useEffect(() => {
    dispatch(fetchCatalogCustomFields());
  }, []);

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={"xl"}
      fullWidth={true}
      PaperProps={{ style: { borderRadius: 15 } }}
    >
      <DialogTitle
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "var(--primary-main)",
          color: "var(--default-light)",
          fontSize: "24px",
          borderRadius: "15px 15px 0 0",
          padding: "16px 24px",
        }}
      >
        <div style={{ fontWeight: "var(--font-semi-bold)" }}>
          Custom Fields for Catalogs
        </div>
        <IconButton
          onClick={handleClose}
          className="closeIconButton"
          style={{ color: "var(--default-light)" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <ShepTabs
        sx={{
          margin: "1px 30px 0px 0",
        }}
        variant="primary"
        value={tabValue}
        onChange={handleTabChange}
      >
        <ShepTab
          variant="primary"
          key="1"
          label={"Add New"}
          id="tab-0"
          aria-controls="tabpanel-0"
          sx={{ width: "310px" }}
        />
        <ShepTab
          variant="primary"
          key="2"
          label={"View All"}
          id="tab-0"
          aria-controls="tabpanel-0"
          sx={{ width: "310px" }}
        />
      </ShepTabs>
      <DialogContent
        className="dialogContent"
        style={{
          backgroundColor: "var(--disabled)",
          padding: "35px",
          minWidth: 400,
        }}
      >
        {tabValue === 0 && <AddEditCustomField open={open} onClose={onClose} />}
        {tabValue === 1 && <ViewCustomFields />}
      </DialogContent>
    </Dialog>
  );
};

interface IAddCustomField {
  open?: boolean;
  onClose: () => void;
  editMode?: boolean;
  selectedField?: ICustomFieldDto | null;
}
/**
 * export interface ICustomFieldDto {
   FieldId: UUIDTypes;
   Code: string;
   Name: string;
   Description: string;
   CatalogType: number;
 }
 */
interface ICustomData {
    FieldCode: string;
    FieldName: string;
    Description: string;
    FieldType: number;
    MaxLength: number;
    MaxDecimal: number;
}
const defaultCustomData: ICustomData = {
    FieldCode: "",
    FieldName: "",
    Description: "",
    FieldType: 1,
    MaxLength: 0,
    MaxDecimal: 0,
  }
const AddEditCustomField = ({ onClose, editMode = false, selectedField = null }: IAddCustomField) => {
  const dispatch = useAppDispatch();
  const [codeError, setCodeError] = useState(false);
  const preloadedData: ICustomData = editMode 
    ? {
      ...defaultCustomData,
      FieldCode: selectedField?.Code ?? "",
      FieldName: selectedField?.Name ?? "",
      Description: selectedField?.Description ?? "",
      FieldType: selectedField?.CatalogType ?? 1
    }
    : defaultCustomData;
  const [customData, setCustomData] = useState<ICustomData>(preloadedData);
  const apiRef = useGridApiContext();
  const [selectedCatalogType, setSelectedCatalogType] = useState(editMode ? selectedField?.CatalogType ?? -1 : -1);

  // const [fieldTypes, setFieldTypes] = useState({ Text: 1 });
   const [selectedFieldType, _setSelectedFieldType] = useState(1);
  // useEffect(() => {
  //   fetchFieldTypes();
  // }, []);

  const validateForm = () => {
    if (!customData.FieldCode || customData.FieldCode === "") {
      return {
        isValid: false,
        message: `Custom Field Code cannot be blank`,
      };
    }
    if (customData.FieldCode === defaultCode) {
      return {
        isValid: false,
        message: `Custom Field Code cannot be saved as ${defaultCode}`,
      };
    }
    if (/\s/.test(customData.FieldCode)) {
      setCodeError(true);
      return {
        isValid: false,
        message: "Custom Field Code cannot contain spaces",
      };
    }

    return { isValid: true };
  };

  const handleClose = () => {
    onClose();
    setCustomData(defaultCustomData);
  };

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setCustomData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // const fetchFieldTypes = async () => {
  //   try {
  //     const response = await axios.get(
  //       "/connectioncatalogartifacts/fieldTypes",
  //     );
  //     setFieldTypes(response.data);
  //   } catch (error) {
  //     dispatch(
  //       createAlert({
  //         status: "Error",
  //         message: "Error fetching field types: " + error.response.data,
  //       }),
  //     );
  //     console.error("Error fetching field types:", error.response.data);
  //   }
  // };

  return (
    <>
      <Container>
        <Grid
          container
          spacing={2}
          rowSpacing={2}
          style={{ marginBottom: "30px", marginTop: "20px" }}
          sx={{
            px: 20,
          }}
        >
          <Grid item xs={12}>
            <Typography>Catalog </Typography>
            <Select
              disabled={editMode}
              value={selectedCatalogType}
              sx={{ width: 250 }}
              onChange={(e: SelectChangeEvent<number>) => {
                setSelectedCatalogType(e.target.value as number ?? -1);
              }}
            >
              <MenuItem key={-1} value={-1}>
                <ShepTooltip
                  title={
                    "This custom field will be available to every catalog type"
                  }
                >
                  <div
                    style={{
                      height: "100%",
                      width: "100%",
                      display: "flex",
                      justifyContent: "left",
                      alignItems: "center",
                    }}
                  >
                    Global
                  </div>
                </ShepTooltip>
              </MenuItem>
              {Object.keys(CatalogTypesExt).map((key) => (
                <MenuItem
                  key={CatalogTypesExt[key].Enum}
                  value={CatalogTypesExt[key].Enum}
                >
                  {CatalogTypesExt[key].Display}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={4}>
            <Typography>
              Field Code <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              disabled={editMode}
              variant="outlined"
              name="FieldCode"
              value={customData?.FieldCode || ""}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                if (/\s/.test(e.target.value)) {
                  setCodeError(true);
                } else {
                  setCodeError(false);
                }
                handleChange(e);
              }}
              error={codeError}
              helperText={
                codeError ? "Custom field code cannot contain spaces" : ""
              }
              sx={{
                width: 250,
                marginTop: 1,
                marginBottom: 2,
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography>Field Name </Typography>
            <TextField
              variant="outlined"
              name="FieldName"
              value={customData?.FieldName || ""}
              onChange={handleChange}
              sx={{
                width: 250,
                marginTop: 1,
                marginBottom: 2,
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography>Description</Typography>
            <TextField
              variant="outlined"
              name="Description"
              value={customData?.Description || ""}
              onChange={handleChange}
              sx={{
                width: 250,
                marginTop: 1,
                marginBottom: 2,
              }}
            />
          </Grid>
        </Grid>
        <Grid
          container
          spacing={2}
          rowSpacing={2}
          style={{ marginBottom: "20px", marginTop: "20px" }}
          sx={{
            px: 20,
          }}
        >
          {/* commented out as restrictions are not enforced in backend yet
          <Grid item xs={12}>
            <Typography variant="subheading">Advanced Fields</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography>Field Type </Typography>
            <Select
              value={selectedFieldType}
              onChange={(e) => {
                setSelectedFieldType(e.target.value ?? 1);
              }}
            >
              {Object.keys(fieldTypes).map((key) => (
                <MenuItem key={fieldTypes[key]} value={fieldTypes[key]}>
                  {key}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={4}>
            <Typography>Max Length </Typography>
            <TextField
              variant="outlined"
              type="number"
              name="MaxLength"
              value={customData?.MaxLength || 0}
              onChange={(e) => {
                const { value } = e.target;
                if (value < 0) e.target.value = 0;
                handleChange(e);
              }}
              inputProps={{ min: 0 }}
              sx={{
                width: 250,
                marginTop: 1,
                marginBottom: 2,
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography>Max Decimal</Typography>
            <TextField
              variant="outlined"
              name="MaxDecimal"
              type="number"
              value={customData?.MaxDecimal || 0}
              onChange={(e) => {
                const { value } = e.target;
                if (value < 0) e.target.value = 0;
                handleChange(e);
              }}
              inputProps={{ min: 0 }}
              sx={{
                width: 250,
                marginTop: 1,
                marginBottom: 2,
              }}
            />
          </Grid> */}
        </Grid>
      </Container>
      <DialogActions style={{ padding: "16px 24px" }}>
        <ShepButton variant="secondary" onClick={handleClose}>
          Cancel
        </ShepButton>
        {/* @ts-expect-error -- props should not be passed through */}
        <ShepButton
          onClick={async () => {
            const validation = validateForm();

            if (!validation.isValid) {
              dispatch(
                createAlert({
                  status: "Error",
                  message: validation.message,
                }),
              );
              return;
            }

            try {
              const FieldData = { ...customData, FieldType: selectedFieldType };
              const request = {
                CatalogType: selectedCatalogType,
                FieldCode: FieldData.FieldCode,
                FieldName: FieldData.FieldName,
                FieldType: FieldData.FieldType,
                Description: FieldData.Description,
                MaxDecimal: FieldData.MaxDecimal,
                MaxLength: FieldData.MaxLength,
                EditMode: editMode,
              };
              const response = await axios.post(
                "/connectioncatalogartifacts/custom-field",
                JSON.stringify(request),
              );
              if (response.data.error === true) {
                dispatch(
                  createAlert({
                    status: "Error",
                    message: response.data.message,
                  }),
                );
              } else {
                dispatch(
                  createAlert({
                    status: "Success",
                    message: "Catalog custom field added successfully",
                  }),
                );
                setCustomData({
                  FieldCode: "",
                  FieldName: "",
                  Description: "",
                  FieldType: 1,
                  MaxLength: 0,
                  MaxDecimal: 0,
                });
                dispatch(fetchCatalogCustomFields());
              }
            } catch (e) {
              console.error({ e });
              dispatch(
                createAlert({
                  status: "Error",
                  message: "Error adding catalog custom field",
                }),
              );
            }

            const { page, pageSize } =
              apiRef.current.state.pagination.paginationModel;

            dispatch(
              fetchCatalogSummaries({
                page,
                pageSize,
                unloadRecords: true,
                search: "",
              }),
            );
            if (editMode) {
              onClose();
            }
          }}
        >
          {editMode ? 'Save' : 'Add'}
        </ShepButton>
      </DialogActions>
    </>
  );
};

interface IOnValueChange {
  id: UUIDTypes;
  code: string;
  value: string;
}
interface IExistingField {
  CatalogCustomFieldId: UUIDTypes;
  FieldValue: string;
}
interface IViewCustomFields {
  canSet?: boolean;
  onValueChange?: (params: IOnValueChange) => void;
  catalogType?: number;
  existingFields?: IExistingField[];
}
export const ViewCustomFields = ({
  canSet = false,
  onValueChange = (_params) => {},
  catalogType = -1,
  existingFields = [],
}: IViewCustomFields) => {
  const customFieldsLoaded = useSelector(selectCatalogCustomFieldsLoaded);
  const customFields = useSelector(selectAllCatalogCustomFields);
  const [filteredCustomFields, setFilteredCustomFields] = useState<ICustomFieldDto[]>([]);
  const viewAll = -2;
  const [searchText, setSearchText] = useState("");
  const [selectedCatalogType, setSelectedCatalogType] = useState(viewAll); //-2 for all values, as -1 is taken for "global" option
  const [selectedCustomField, setSelectedCustomField] = useState<ICustomFieldDto | null>(null);
  const gridRef = useGridApiRef();

  const dispatch = useAppDispatch();
  const baseColumns = [
    {
      field: "Code",
      headerName: "Code",
      width: 300,
    },
    {
      field: "Name",
      headerName: "Name",
      width: 300,
    },
    {
      field: "Description",
      headerName: "Description",
      width: 300,
    },
    {
      field: "CatalogType",
      headerName: "Catalog Type",
      width: 300,
      renderCell: (params: GridRenderCellParams<ICustomFieldDto>) => {
        const catalogName =
          params.value < 1
            ? "Global"
            : getCatalogDisplayInfo(params.value).Display;
        return catalogName;
      },
    },
  ];

  const valueColumns = [
    {
      field: "Value",
      headerName: "Value",
      width: 300,
      renderCell: (params: GridRenderCellParams<ICustomFieldDto>) => {
        return (
          <ClickableEditor
            value={params.value}
            valueChange={(value: string) => {
              onValueChange({
                id: params.row.FieldId,
                code: params.row.Code,
                value: value,
              });
            }}
          />
        );
      },
    },
  ];

  const actionsColumns = [
    {
      field: "actions",
      headerName: "",
      width: 120,
      sortable: false,
      filterable: false,
      resizable: false,
      renderCell: (params: GridRenderCellParams<ICustomFieldDto>) => (
        <>
          <IconButton onClick={() => setSelectedCustomField(params.row)}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => handleDelete(params)}>
            <DeleteBinFill />
          </IconButton>
        </>
      ),
    },
  ];

  const columns = canSet
    ? [...baseColumns, ...valueColumns]
    : [...baseColumns, ...actionsColumns];

  useEffect(() => {
    let filteredFields =
      selectedCatalogType === viewAll
        ? canSet
          ? [...(customFields[catalogType] ?? []), ...(customFields[-1] ?? [])]
          : Object.values(customFields).flat()
        : [...(customFields[selectedCatalogType] ?? [])];

    if (searchText !== "") {
      filteredFields = filteredFields.filter((x) =>
        x.Code?.toUpperCase().includes(searchText.toUpperCase()),
      );
    }

    if (canSet) {
      filteredFields = filteredFields.map((x) => ({
        ...x,
        Value:
          existingFields.find((y) => y.CatalogCustomFieldId === x.FieldId)
            ?.FieldValue ?? "",
      }));
    }
    setFilteredCustomFields(filteredFields);
  }, [customFields, selectedCatalogType, searchText]);

  const handleDelete = (params: GridRenderCellParams<ICustomFieldDto> | null, batch: boolean = false) => {
    if (!batch && params) {
      deleteRows([params.row.FieldId]);
    } else {
      const map = gridRef?.current.getSelectedRows();
      const selectedRows = Array.from(map, ([_name, value]) => ({ ...value }));
      if (!selectedRows?.length) {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: "Please select at least one record.",
          }),
        );
        return;
      }
      let selectedRowsToDelete = selectedRows.map((row) => row.FieldId);
      deleteRows(selectedRowsToDelete);
    }
  };

  const deleteRows = (selectedRowsToDelete: UUIDTypes[]) => {
    axios
      .delete("/connectioncatalogartifacts/custom-field", {
        data: selectedRowsToDelete,
      })
      .then((response) => {
        if (response?.data.Warning) {
          dispatch(
            createAlert({
              status: "Warning",
              alertType: "result",
              message: response?.data.Message,
            }),
          );
        } else {
          dispatch(
            createAlert({
              status: "Success",
              alertType: "single",
              message: "Catalog custom field deleted successfully",
            }),
          );
        }
        dispatch(fetchCatalogCustomFields());
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "result",
            message: "Error deleting custom field: " + error.response.data,
          }),
        );
        console.error("Error deleting custom field:", error.response.data);
      });
  };

  const getCatalogDisplayInfo = (dataType: number) => {
    const catalogTypeInfoKey = Object.keys(CatalogTypesExt).find(
      (key) => dataType === CatalogTypesExt[key].Enum,
    ) as string;
    return CatalogTypesExt[catalogTypeInfoKey];
  };

  const onClose = () => {
    setSelectedCustomField(null);
  }
  return  selectedCustomField != null ?
  (
    <AddEditCustomField onClose={onClose} editMode selectedField={selectedCustomField}/>
  )
  : (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "15px",
            marginBottom: "10px",
          }}
        >
          <TextField
            variant="outlined"
            label="Search"
            value={searchText}
            onChange={(e) => {
              const { value } = e.target;
              setSearchText(value);
            }}
            sx={{
              width: 250,
            }}
          />
          <Select
            value={selectedCatalogType}
            sx={{ width: 250 }}
            onChange={(e: SelectChangeEvent<number>) => {
              setSelectedCatalogType(e.target.value as number ?? -1);
            }}
          >
            <MenuItem key={viewAll} value={viewAll}>
              View All
            </MenuItem>
            <MenuItem key={-1} value={-1}>
              Global
            </MenuItem>
            {canSet ? (
              <MenuItem key={catalogType} value={catalogType}>
                {getCatalogDisplayInfo(catalogType).Display}
              </MenuItem>
            ) : (
              Object.keys(CatalogTypesExt).map((key) => (
                <MenuItem
                  key={CatalogTypesExt[key].Enum}
                  value={CatalogTypesExt[key].Enum}
                >
                  {CatalogTypesExt[key].Display}
                </MenuItem>
              ))
            )}
          </Select>
        </Box>

        {!canSet && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "10px",
            }}
          >
            <ShepHorizontalMenu
              actions={[
                {
                  label: "Delete",
                  onClick: () => handleDelete(null, true),
                  variant: "danger",
                },
              ]}
            />
          </Box>
        )}
      </Box>

      <ShepDataGridPro
        isDataLoaded={customFieldsLoaded}
        rows={filteredCustomFields}
        columns={columns}
        apiRef={gridRef}
        columnHeaderHeight={78}
        rowHeight={100}
        rowCount={filteredCustomFields.length}
        getRowId={(row: ICustomFieldDto) => row.FieldId}
        whiteHeader
        checkboxSelection
        disableMultipleRowSelection={false}
        disableColumnMenu
        disableColumnReorder
        hideFooterSelectedRowCount
        disableRowSelectionOnClick
        pagination
        slots={{
          pagination: () => ShepPagination({ pageOptions: [5, 10, 15] }),
        }}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 5, page: 0 },
          },
          pinnedColumns: {
            right: ["actions"],
          },
        }}
        sx={{
          color: "var(--text-primary)",
          fontSize: "var(--font-sm)",
          fontFamily: "var(--font-family)",
          borderTopLeftRadius: "12px",
          borderTopRightRadius: "12px",
          backgroundColor: "var(--default-light)",
          border: "none",
          "& .MuiDataGrid:hover": {
            backgroundColor: "inherit",
          },
          "& .MuiDataGrid-cellCheckbox, & .workflow-row": {
            opacity: 0,
            borderBottom: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "var(--tertiary-13)",
          },
          "& .Mui-selected": {
            backgroundColor: "var(--primary-pale)",
          },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader.MuiDataGrid-columnHeaderCheckbox":
            {
              outline: "none",
            },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox, & .MuiDataGrid-row:hover .workflow-row":
            {
              opacity: 1,
            },
          "& .MuiDataGrid-cellCheckbox": {
            borderLeft: "8px solid transparent",
          },
          "& .Mui-selected .MuiDataGrid-cellCheckbox": {
            opacity: 1,
            borderLeft: "8px solid var(--primary-main)",
            borderTopLeftRadius: "4px",
            borderBottomLeftRadius: "4px",
          },
          "& .MuiDataGrid-columnHeaderCheckbox": {
            outline: "none",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontSize: "var(--font-md)",
            fontWeight: "var(--font-bold)",
            borderLeft: "none",
          },
          "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover": {
            borderBottomLeftRadius: "4px",
            borderBottomRightRadius: "4px",
            backgroundColor: "var(--primary-pale)",
            borderBottom: "none",
            borderLeft: "none",
          },
          "& .MuiDataGrid-pinnedColumnHeaders.MuiDataGrid-pinnedColumnHeaders--right":
            {
              display: "none",
            },
        }}
      />
    </>
  );
};
interface IClickableEditor {
  value: string;
  valueChange: (value: string) => void;
}

const ClickableEditor = forwardRef(({ value: initialValue, valueChange }: IClickableEditor) => {
  const [value, setValue] = useState(initialValue || "");
  const [error, setError] = useState(false);

  useEffect(() => {
    if (initialValue.trim() && !value.trim()) {
      setError(true);
    } else {
      setError(false);
    }
  }, [value]);

  const handleBlur = () => {
    valueChange(value);
  };

  return (
    <>
      <TextField
        variant="outlined"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
        onBlur={handleBlur}
        error={error}
        helperText={error ? "Custom Field will be removed from Catalog" : ""}
        sx={{
          width: 200,
        }}
      />
    </>
  );
});
export default CustomCatalogFieldPopup;
